import {useEffect} from 'react'
import {createSelector} from 'reselect'
import classNames from 'classnames'
import kebabCase from 'lodash/kebabCase.js'

import {
  usesInventorySelector,
  hasUseMOsFeatureSelector,
  useKittingSelector,
  useManualTrackingSelector,
  usePurchaseOrdersSelector,
  useDropshippingSelector,
  usesDataImportTasksSelector,
} from '../../data/company.js'
import {hasProductUpdateInventoryPermissionSelector} from '../../data/me.js'

import AddressStrategy from './strategies/AddressStrategy/index.js'
import BatchStrategy from './strategies/BatchStrategy/index.js'
import BOMStrategy from './strategies/BOMStrategy/index.js'
import SupplierSKUStrategy from './strategies/SupplierSKUStrategy/index.js'
import SupplierStrategy from './strategies/SupplierStrategy/index.js'
import ProductArchiveStrategy from './strategies/ProductArchiveStrategy/index.js'
import ProductUnarchiveStrategy from './strategies/ProductUnarchiveStrategy/index.js'
import ProductCartBridgeStrategy from './strategies/ProductCartBridgeStrategy/index.js'
import ProductCreateStrategy from './strategies/ProductCreateStrategy/index.js'
import ProductImageStrategy from './strategies/ProductImageStrategy/index.js'
import ProductUpdateStrategy from './strategies/ProductUpdateStrategy/index.js'
import ProductFulfillmentStrategy from './strategies/ProductFulfillmentStrategy/index.js'
import ProductTagStrategy from './strategies/ProductTagStrategy/index.js'
import PurchaseOrderStrategy from './strategies/PurchaseOrderStrategy/index.js'
import RemoveSupplierSKUStrategy from './strategies/RemoveSupplierSKUStrategy/index.js'
import TrackingStrategy from './strategies/TrackingStrategy/index.js'
import KitStrategy from './strategies/KitStrategy/index.js'
import MOCreateStrategy from './strategies/MOCreateStrategy/index.js'
import InventoryStrategy from './strategies/InventoryStrategy/index.js'
import OrderStrategy from './strategies/OrderStrategy/index.js'
import NoPermissionsPage from '../NoPermissionsPage.js'
import {
  dataImportSelector,
  canUseDataImportSelector,
  setupDataImportForm,
} from './dataImportFunctions.js'
import {useSuppliersSelector} from '../../data/suppliers.js'
import {canUseBatchesSelector} from '../../data/batches.js'
import {canUseMOsSelector} from '../../data/mos.js'
import ProductCreateImport from './imports/ProductCreateImport.js'
import ProductUpdateImport from './imports/ProductUpdateImport.js'
import KitImport from './imports/KitImport.js'
import {dispatch, useAutoForm, useSelector} from '../../store.js'
import {DATA_IMPORT} from './DataImportLocationListener.js'
import {getNounsFromValue} from '../../common/tokenizeOptions.js'
import {setCurrent} from '../../redux/actions/ui/dataImport/index.js'

const STRATEGY_CLASSES = [
  ProductCreateStrategy,
  ProductUpdateStrategy,
  PurchaseOrderStrategy,
  OrderStrategy,
  TrackingStrategy,
  InventoryStrategy,
  SupplierSKUStrategy,
  RemoveSupplierSKUStrategy,
  KitStrategy,
  MOCreateStrategy,
  BatchStrategy,
  BOMStrategy,
  SupplierStrategy,
  AddressStrategy,
  ProductArchiveStrategy,
  ProductUnarchiveStrategy,
  ProductFulfillmentStrategy,
  ProductCartBridgeStrategy,
  ProductImageStrategy,
  ProductTagStrategy,
]

const IMPORT_COMPONENTS = [
  ProductCreateImport,
  ProductUpdateImport,
  PurchaseOrderStrategy,
  OrderStrategy,
  TrackingStrategy,
  InventoryStrategy,
  SupplierSKUStrategy,
  RemoveSupplierSKUStrategy,
  KitImport,
  MOCreateStrategy,
  BatchStrategy,
  BOMStrategy,
  SupplierStrategy,
  AddressStrategy,
  ProductArchiveStrategy,
  ProductUnarchiveStrategy,
  ProductFulfillmentStrategy,
  ProductCartBridgeStrategy,
  ProductImageStrategy,
  ProductTagStrategy,
]

const IMPORT_CATEGORY_OPTIONS = [
  {value: 'orders', display: 'Orders', disabled: true},
  {value: 'products', display: 'Products', disabled: true},
  {value: 'kits_boms', display: 'Kits / BOMs', disabled: true},
  {value: 'inventory', display: 'Inventory', disabled: true},
  {value: 'purchase_orders', display: 'Purchase Orders', disabled: true},
  {value: 'suppliers', display: 'Suppliers', disabled: true},
  {value: 'manufacturing', display: 'Manufacturing', disabled: true},
]

const importOptionsSelector = createSelector(
  usesInventorySelector,
  hasProductUpdateInventoryPermissionSelector,
  hasUseMOsFeatureSelector,
  useKittingSelector,
  usePurchaseOrdersSelector,
  useDropshippingSelector,
  useManualTrackingSelector,
  useSuppliersSelector,
  canUseBatchesSelector,
  canUseMOsSelector,
  usesDataImportTasksSelector,
  (
    usesInventory,
    hasProductUpdateInventoryPermission,
    hasUseMOsFeature,
    useKitting,
    usePurchaseOrders,
    useDropshipping,
    useManualTracking,
    useSuppliers,
    canUseBatches,
    canUseMOs,
    usesDataImportTasks,
  ) => {
    let importComponents = usesDataImportTasks
      ? IMPORT_COMPONENTS
      : STRATEGY_CLASSES

    if (!hasProductUpdateInventoryPermission) {
      importComponents = importComponents.filter(
        (strategyClass) => strategyClass !== InventoryStrategy,
      )
    }

    if (!usesInventory) {
      importComponents = importComponents.filter(
        (strategyClass) => strategyClass !== BOMStrategy,
      )
    }

    if (!usesInventory && !useDropshipping) {
      importComponents = importComponents.filter(
        (strategyClass) => strategyClass !== ProductFulfillmentStrategy,
      )
    }

    if (!useKitting) {
      importComponents = importComponents.filter(
        (strategyClass) => strategyClass !== KitStrategy,
      )
    }

    if (!usePurchaseOrders) {
      importComponents = importComponents.filter(
        (strategyClass) => strategyClass !== PurchaseOrderStrategy,
      )
    }

    if (!useSuppliers) {
      importComponents = importComponents.filter(
        (strategyClass) =>
          ![
            SupplierSKUStrategy,
            RemoveSupplierSKUStrategy,
            SupplierStrategy,
          ].includes(strategyClass),
      )
    }

    if (!hasUseMOsFeature) {
      importComponents = importComponents.filter(
        (strategyClass) => strategyClass !== BOMStrategy,
      )
    }

    if (!useManualTracking) {
      importComponents = importComponents.filter(
        (strategyClass) => strategyClass !== TrackingStrategy,
      )
    }

    if (!canUseBatches) {
      importComponents = importComponents.filter(
        (strategyClass) => strategyClass !== BatchStrategy,
      )
    }

    if (!canUseMOs) {
      importComponents = importComponents.filter(
        (strategyClass) => strategyClass !== MOCreateStrategy,
      )
    }

    const options = []

    for (const categoryOption of IMPORT_CATEGORY_OPTIONS) {
      const list = importComponents
        .filter(({option}) => option.category === categoryOption.value)
        .map(({option}) => option)

      if (list.length) {
        options.push({
          ...categoryOption,
          nouns: getNounsFromValue(
            [
              categoryOption.value,
              ...list.reduce((prev, {nouns}) => {
                prev.push(...nouns)
                return prev
              }, []),
            ].join(' '),
          ),
        })
        options.push(...list)
      }
    }

    return options
  },
)

export default function DataImport() {
  useAutoForm(DATA_IMPORT, setupDataImportForm)

  const currentStrategy = useSelector(dataImportSelector).current
  const importOptions = useSelector(importOptionsSelector)
  const canUseDataImport = useSelector(canUseDataImportSelector)

  const CurrentStrategyClass = importOptions.find(
    (option) => option.value === currentStrategy,
  )?.Component

  useEffect(() => {
    if (!CurrentStrategyClass) {
      // find the old sticky value / remove after some years
      const option = importOptions.find(
        ({display}) => display === currentStrategy,
      )

      dispatch(setCurrent(option?.value || importOptions[0].value))
    }
  }, [CurrentStrategyClass])

  useEffect(() => {
    document.title = 'Data Import'
  }, [])

  if (!canUseDataImport) {
    return <NoPermissionsPage />
  }

  if (!CurrentStrategyClass) {
    return null
  }

  return (
    <div
      className={classNames(
        'meta-csv-div-dataimport',
        `meta-csv-strategy-${kebabCase(currentStrategy)}`,
      )}
    >
      <CurrentStrategyClass importOptions={importOptions} />
    </div>
  )
}
